import apiClient from "./axiosInstance"

export interface HashtagPipelineFlowData {
  hashtagManagers: {
    total: number
    active: number
    completed: number
  }
  runpodWorkers: {
    active: number
    running: number
    idle: number
  }
  hashtagPostCounts: {
    totalHashtagPosts: number
    savedHashtagPosts: number
    unsavedHashtagPosts: number
    postsWithTags: number
    hashtagPostsNeedingRunpodAnalysis: number
    hashtagPostsRunpodAnalyzed: number
    hashtagPostsNeedingGptAnalysis: number
    hashtagPostsGptAnalyzed: number
    hashtagPostsNeedingProductEmbedding: number
    hashtagPostsProductEmbedding: number
    hashtagPostsEligibleForPineconeEmbedding: number
    hashtagPostsPineconeEmbedded: number
    hashtagPostsWaitingPineconeEmbedding: number
  }
  influencers: {
    total: number
    totalInfluencerPosts: number
    savedInfluencerPosts: number
    unsavedInfluencerPosts: number
  }
  hashtagBrands: {
    total: number
    totalHashtagBrandPosts: number
    savedHashtagBrandPosts: number
    unsavedHashtagBrandPosts: number
    hashtagBrandPostsNeedingRunpodAnalysis: number
    hashtagBrandPostsRunpodAnalyzed: number
    hashtagBrandPostsNeedingGptAnalysis: number
    hashtagBrandPostsGptAnalyzed: number
  }
  lastUpdated: string
}

export interface RunpodPod {
  id: string
  name: string
  uptimeSeconds: number
  gpuCount: number
}

export interface RunpodPodsResponse {
  pods: RunpodPod[]
  totalPods: number
  lastUpdated: string
}

interface PipelineFlowApiResponse {
  type?: string
  data?: Partial<HashtagPipelineFlowData>
  lastUpdated?: string | Date | { $date?: string }
}

const toIsoDate = (value: unknown): string => {
  if (typeof value === "string") {
    return value
  }
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === "object" && value !== null && "$date" in value) {
    const dateValue = (value as { $date?: unknown }).$date
    if (typeof dateValue === "string") {
      return dateValue
    }
  }
  return new Date().toISOString()
}

const getNumber = (value: unknown): number => (typeof value === "number" ? value : 0)

const normalizeFlowData = (
  data: Partial<HashtagPipelineFlowData> | undefined,
): HashtagPipelineFlowData => ({
  hashtagManagers: {
    total: getNumber(data?.hashtagManagers?.total),
    active: getNumber(data?.hashtagManagers?.active),
    completed: getNumber(data?.hashtagManagers?.completed),
  },
  runpodWorkers: {
    active: getNumber(data?.runpodWorkers?.active),
    running: getNumber(data?.runpodWorkers?.running),
    idle: getNumber(data?.runpodWorkers?.idle),
  },
  hashtagPostCounts: {
    totalHashtagPosts: getNumber(data?.hashtagPostCounts?.totalHashtagPosts),
    savedHashtagPosts: getNumber(data?.hashtagPostCounts?.savedHashtagPosts),
    unsavedHashtagPosts: getNumber(data?.hashtagPostCounts?.unsavedHashtagPosts),
    postsWithTags: getNumber(data?.hashtagPostCounts?.postsWithTags),
    hashtagPostsNeedingRunpodAnalysis: getNumber(
      data?.hashtagPostCounts?.hashtagPostsNeedingRunpodAnalysis,
    ),
    hashtagPostsRunpodAnalyzed: getNumber(data?.hashtagPostCounts?.hashtagPostsRunpodAnalyzed),
    hashtagPostsNeedingGptAnalysis: getNumber(
      data?.hashtagPostCounts?.hashtagPostsNeedingGptAnalysis,
    ),
    hashtagPostsGptAnalyzed: getNumber(data?.hashtagPostCounts?.hashtagPostsGptAnalyzed),
    hashtagPostsNeedingProductEmbedding: getNumber(
      data?.hashtagPostCounts?.hashtagPostsNeedingProductEmbedding,
    ),
    hashtagPostsProductEmbedding: getNumber(data?.hashtagPostCounts?.hashtagPostsProductEmbedding),
    hashtagPostsEligibleForPineconeEmbedding: getNumber(
      data?.hashtagPostCounts?.hashtagPostsEligibleForPineconeEmbedding,
    ),
    hashtagPostsPineconeEmbedded: getNumber(data?.hashtagPostCounts?.hashtagPostsPineconeEmbedded),
    hashtagPostsWaitingPineconeEmbedding: getNumber(
      data?.hashtagPostCounts?.hashtagPostsWaitingPineconeEmbedding,
    ),
  },
  influencers: {
    total: getNumber(data?.influencers?.total),
    totalInfluencerPosts: getNumber(data?.influencers?.totalInfluencerPosts),
    savedInfluencerPosts: getNumber(data?.influencers?.savedInfluencerPosts),
    unsavedInfluencerPosts: getNumber(data?.influencers?.unsavedInfluencerPosts),
  },
  hashtagBrands: {
    total: getNumber(data?.hashtagBrands?.total),
    totalHashtagBrandPosts: getNumber(data?.hashtagBrands?.totalHashtagBrandPosts),
    savedHashtagBrandPosts: getNumber(data?.hashtagBrands?.savedHashtagBrandPosts),
    unsavedHashtagBrandPosts: getNumber(data?.hashtagBrands?.unsavedHashtagBrandPosts),
    hashtagBrandPostsNeedingRunpodAnalysis: getNumber(
      data?.hashtagBrands?.hashtagBrandPostsNeedingRunpodAnalysis,
    ),
    hashtagBrandPostsRunpodAnalyzed: getNumber(
      data?.hashtagBrands?.hashtagBrandPostsRunpodAnalyzed,
    ),
    hashtagBrandPostsNeedingGptAnalysis: getNumber(
      data?.hashtagBrands?.hashtagBrandPostsNeedingGptAnalysis,
    ),
    hashtagBrandPostsGptAnalyzed: getNumber(data?.hashtagBrands?.hashtagBrandPostsGptAnalyzed),
  },
  lastUpdated: typeof data?.lastUpdated === "string" ? data.lastUpdated : new Date().toISOString(),
})

export const TAgetComposedPipelineFlow = async (): Promise<HashtagPipelineFlowData> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/pipeline-flow", {
    timeout: 30000,
  })

  if (response.status !== 200) {
    throw new Error("Pipeline flow verisi alınamadı")
  }

  const payload = response.data as PipelineFlowApiResponse | HashtagPipelineFlowData
  const flowData =
    "data" in (payload as PipelineFlowApiResponse)
      ? (payload as PipelineFlowApiResponse).data
      : payload
  const normalized = normalizeFlowData(flowData as Partial<HashtagPipelineFlowData>)

  const responseLastUpdated =
    "data" in (payload as PipelineFlowApiResponse)
      ? (payload as PipelineFlowApiResponse).lastUpdated
      : undefined

  const dataLastUpdated =
    typeof flowData === "object" && flowData !== null && "lastUpdated" in flowData
      ? (flowData as { lastUpdated?: unknown }).lastUpdated
      : undefined

  return {
    ...normalized,
    lastUpdated: toIsoDate(dataLastUpdated ?? responseLastUpdated ?? normalized.lastUpdated),
  }
}

// Aktif RunPod podlarını getir
export const TAgetActiveRunpodPods = async (): Promise<RunpodPodsResponse> => {
  const response = await apiClient.get("/admin/hashtag-pipeline/runpod-pods", {
    timeout: 15000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("RunPod podları alınamadı")
  }
}

// Tek pod terminate et
export const TAterminateRunpodPod = async (podId: string) => {
  const response = await apiClient.delete(`/admin/hashtag-pipeline/runpod-pods/${podId}`, {
    timeout: 15000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Pod terminate edilemedi")
  }
}

// Tüm podları terminate et
export const TAterminateAllRunpodPods = async () => {
  const response = await apiClient.delete("/admin/hashtag-pipeline/runpod-pods", {
    timeout: 30000,
  })
  if (response.status === 200) {
    return response.data
  } else {
    throw new Error("Podlar terminate edilemedi")
  }
}
